import { AxiosInstance } from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";

import * as requestUrl from "../../constants/request";
import { extractError } from "../../utility/utils";

import {
  AccountInfo,
  FetchAccountInfoReqDTO,
  FetchNonValidatedDTO,
} from "./account-state";
import { addNotification } from "store/notification/notification-utils";
import { ThunkAPIConfig } from "store";

export type FetchAccounts = typeof fetchAccounts;

export const fetchAccounts = createAsyncThunk<
  AccountInfo[],
  { request: AxiosInstance; data?: FetchAccountInfoReqDTO },
  ThunkAPIConfig
>("account/fetchAccounts", async ({ request, data }, thunkapi) => {
  try {
    const result = await request(requestUrl.FT_QUICK_PAY_LOAN_FETCH, {
      method: data ? "POST" : "GET",
      data: data?.accountQueryParameter,
      params: data && {
        clientId: data?.clientId,
      },
    });
    return result.data;
  } catch (e) {
    const errKey = extractError(e, "no_data_found");
    const { isSlideOpen } = thunkapi.getState().common;
    addNotification(thunkapi.dispatch, isSlideOpen, {
      messages: errKey,
      type: "ERROR",
      closable: true,
    });
    return thunkapi.rejectWithValue(null);
  }
});

export type FetchNonValidatedAccount = typeof fetchNonValidatedAccount;

export const fetchNonValidatedAccount = createAsyncThunk<
  AccountInfo[],
  { request: AxiosInstance; data: FetchNonValidatedDTO },
  ThunkAPIConfig
>("account/fetchNonValidatedAccount", async ({ request, data }, thunkapi) => {
  try {
    const result = await request(requestUrl.FT_QUICK_PAY_NON_VALIDATED_FETCH, {
      method: "POST",
      data: {
        referenceNumber: data.referenceNumber,
        accountType: data.accountType,
        captchaToken: data.captchaToken,
        captchaVersion: data.captchaVersion, // 'v3' or 'v2' — backend selects correct secret key
        challengeId: data.challengeId, // ties v3 and v2 challenges together in validation log
      },
      params: { clientId: data.clientId },
    });
    const accountInfo: AccountInfo[] = Array.isArray(result.data)
      ? result.data
      : [result.data];
    return accountInfo;
  } catch (e) {
    const status = (e as any)?.response?.status;
    const responseBody = (e as any)?.response?.data;

    // 428: borderline CAPTCHA score — let component handle v2 escalation silently (includes challengeId)
    // 403: hard CAPTCHA reject — let component handle error display silently
    if (status !== 428 && status !== 403) {
      const errKey = extractError(e, "no_data_found");
      addNotification(thunkapi.dispatch, false, {
        messages: errKey,
        type: "ERROR",
        closable: true,
      });
    }

    return thunkapi.rejectWithValue({
      status,
      challengeId: responseBody?.challengeId,
    });
  }
});
